import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft, Send, User } from 'lucide-react';
import { getAuthHeaders, getUser } from '../lib/auth';
import dayjs from 'dayjs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https:', 'wss:').replace('http:', 'ws:');

const Chat = () => {
  const { otherUserId } = useParams();
  const navigate = useNavigate();
  const currentUser = getUser();
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [ws, setWs] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadOtherUser();
    loadMessages();
    connectWebSocket();

    return () => {
      if (ws) ws.close();
    };
  }, [otherUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadOtherUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${otherUserId}`, {
        headers: getAuthHeaders()
      });
      setOtherUser(response.data);
    } catch (error) {
      console.error('Failed to load user');
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/${otherUserId}`, {
        headers: getAuthHeaders()
      });
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const connectWebSocket = () => {
    const websocket = new WebSocket(`${WS_URL}/ws/chat/${currentUser.id}`);
    
    websocket.onopen = () => {
      console.log('Connected to chat');
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setMessages(prev => [...prev, data]);
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    setWs(websocket);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !ws) return;

    try {
      // Send via REST API (which also saves to DB)
      await axios.post(`${API}/chat/send?toUserId=${otherUserId}&text=${encodeURIComponent(inputText)}`, {}, {
        headers: getAuthHeaders()
      });

      // Also send via WebSocket for real-time
      ws.send(JSON.stringify({
        toUserId: otherUserId,
        text: inputText
      }));

      setInputText('');
    } catch (error) {
      console.error('Failed to send message');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/browse')}
              data-testid="back-browse-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              {otherUser?.profilePicUrl ? (
                <img src={otherUser.profilePicUrl} className="w-10 h-10 rounded-full" alt={otherUser.name} />
              ) : (
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-500" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  {otherUser?.name || 'User'}
                </h2>
                <p className="text-sm text-slate-600" data-testid="chat-partner-name">
                  {otherUser?.businessName || 'Professional'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg, idx) => {
              const isOwnMessage = msg.fromUserId === currentUser.id;
              return (
                <div
                  key={idx}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${idx}`}
                >
                  <div
                    className={`max-w-md px-4 py-3 rounded-2xl ${
                      isOwnMessage
                        ? 'bg-slate-900 text-white'
                        : 'bg-white text-slate-900 border border-slate-200'
                    }`}
                  >
                    <p className="text-sm" data-testid={`message-text-${idx}`}>{msg.text}</p>
                    <span className="text-xs opacity-50 mt-1 block">
                      {msg.createdAt ? dayjs(msg.createdAt).format('HH:mm') : ''}
                    </span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="flex gap-2" data-testid="chat-form">
            <Input
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Type a message..."
              className="flex-1"
              data-testid="input-message"
            />
            <Button
              type="submit"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;
